import {
  Badge,
  Button,
  Column,
  Divider,
  MeatballMenu,
  Modal,
  Row,
  Text,
  Tooltip,
  useDesignTokens,
} from "@gradience/ui";
import { IconContainer } from "@gradience/ui/dist/components/icon";
import { isApiError, useApiPost, useApiQuery } from "../../../../lib/api";
import { useSelectedTest } from "../../../../domain/use-latest-test";
import { displayDateNoYear } from "../../../../lib/display-date";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useAppForm } from "../../../../components/forms";
import { displayCurrency } from "../../../../lib/display-currency";
import { useStore } from "@tanstack/react-form";
import { ChargedTo } from "./charged-to";

export const NotRegistered = () => {
  const selectedTest = useSelectedTest();
  const registration = useApiQuery(
    "/registrations/:testId",
    { testId: selectedTest.data?.id ?? "" },
    { enabled: selectedTest.data?.id !== undefined }
  );
  const school = useApiQuery("/school", {});

  const navigate = useNavigate({
    from: "/$test-slug/home",
  });
  const search = useSearch({ from: "/$test-slug/home/" });
  const registerMutation = useApiPost("/registrations/:testId", {
    testId: selectedTest.data?.id ?? "",
  });

  const isEarly =
    !!registration.data?.earlyRegistrationCutoffDate &&
    new Date() < new Date(registration.data.earlyRegistrationCutoffDate);

  const form = useAppForm({
    defaultValues: { numberOfStudents: undefined, memberCode: "" } as {
      numberOfStudents: number | undefined;
      memberCode: string;
    },
    onSubmit: async ({ value }) => {
      if (!value.numberOfStudents)
        throw new Error("Must enter a number of students");

      await registerMutation.mutateAsync({
        body: {
          //  this is a string for some reason
          numberOfStudents: parseInt(
            value.numberOfStudents as unknown as string
          ),
          isEarlyRegistration: isEarly,
          memberCode: value.memberCode.trim() || undefined,
        },
      });
      registration.refetch();
      navigate({ search: { modal: undefined } });
    },
  });

  const examFee = registration.data?.examFeeAmount;
  const hasMemberPricing =
    registration.data?.memberPerStudentAmount !== null &&
    registration.data?.memberPerStudentAmount !== undefined;

  const discountAmount = isEarly
    ? (registration.data?.examFeeAmount ?? 0) -
      (registration.data?.examFeeEarlyRegistrationAmount ?? 0)
    : 0;

  // The code is validated when registering; until then, show the member price
  // as soon as one is entered.
  const perStudentAmount = useStore(form.store, (form) =>
    hasMemberPricing && form.values.memberCode.trim()
      ? (registration.data?.memberPerStudentAmount ?? 0)
      : (registration.data?.perStudentAmount ?? 0)
  );

  const total = useStore(form.store, (form) => {
    return (
      (examFee ?? 0) +
      (form.values.numberOfStudents ?? 0) * perStudentAmount -
      discountAmount
    );
  });

  const designTokens = useDesignTokens();

  if (!registration.data || !examFee) {
    return null;
  }

  return (
    <Column gap={24}>
      <Row
        gap={16}
        style={{
          alignItems: "flex-start",
        }}
      >
        <IconContainer icon="card-02" />
        <Column style={{ flex: 1 }}>
          <Text textStyle="20px - Bold">Registration</Text>
          <Text textStyle="14px - Medium">
            <Row gap={4} style={{ alignItems: "center" }}>
              <Text textStyle="14px - Medium">Not Registered</Text>
              {isEarly ? (
                <Badge
                  type="warning"
                  icon="information-circle-contained"
                  children={`Register before ${displayDateNoYear(registration.data.earlyRegistrationCutoffDate)} for a discount`}
                />
              ) : null}
            </Row>
          </Text>
        </Column>
        <MeatballMenu
          options={[
            {
              label: "Edit payment method",
              value: "edit-payment-method",
            },
          ]}
          onSelect={(value) => {
            switch (value) {
              case "edit-payment-method":
                navigate({
                  search: {
                    modal: "billing-method" as any,
                  },
                });
                return;
            }
          }}
        />
      </Row>
      <Button
        text="Register for the ULE"
        leadingIcon="plus-01"
        variant="subdued"
        onPress={() => navigate({ search: { modal: "register" } })}
      />
      <Modal
        open={search.modal === "register"}
        close={() => navigate({ search: { modal: undefined } })}
      >
        <form
          style={{ display: "flex", flexDirection: "column", gap: 32 }}
          onSubmit={(e) => {
            e.preventDefault();
            form.handleSubmit();
          }}
        >
          <Text textStyle="24px - Bold">Register for the ULE</Text>
          <Column gap={8}>
            <Text textStyle="16px - Semibold">Number of Students</Text>
            <Text textStyle="14px - Medium">
              Enter the number of students who will take the ULE. You can add to
              this number later if necessary. After registering, you or another
              user can add students to groups.
            </Text>
            <form.AppField
              name="numberOfStudents"
              children={(field) => (
                <field.InputField
                  type="number"
                  placeholder="Number of students"
                />
              )}
            />
          </Column>
          {hasMemberPricing && (
            <Column gap={8}>
              <Text textStyle="16px - Semibold">Member Code (optional)</Text>
              <Text textStyle="14px - Medium">
                Member schools pay{" "}
                {displayCurrency(registration.data.memberPerStudentAmount!)} per
                student instead of{" "}
                {displayCurrency(registration.data.perStudentAmount)}. Enter the
                code you received to apply member pricing.
              </Text>
              <form.AppField
                name="memberCode"
                children={(field) => (
                  <field.InputField placeholder="Member code" />
                )}
              />
            </Column>
          )}
          <form.Subscribe
            selector={(form) => form.values.numberOfStudents ?? 0}
          >
            {(numberOfStudents) => (
              <Column gap={16}>
                <Column gap={8}>
                  <Row>
                    <Text textStyle="14px - Medium" style={{ flex: 1 }}>
                      Students
                    </Text>
                    <Text textStyle="14px - Medium">{numberOfStudents}</Text>
                  </Row>
                  <Row>
                    <Text textStyle="14px - Medium" style={{ flex: 1 }}>
                      Price per student
                    </Text>
                    <Text textStyle="14px - Medium">
                      {displayCurrency(perStudentAmount)}
                    </Text>
                  </Row>
                </Column>
                <Divider />
                <Column gap={8}>
                  <Row>
                    <Text textStyle="14px - Medium" style={{ flex: 1 }}>
                      Subtotal
                    </Text>
                    <Text textStyle="14px - Medium">
                      {displayCurrency(perStudentAmount * numberOfStudents)}
                    </Text>
                  </Row>
                  <Row>
                    <Text textStyle="14px - Medium" style={{ flex: 1 }}>
                      Exam Fee
                    </Text>
                    <Text textStyle="14px - Medium">
                      {displayCurrency(examFee)}
                    </Text>
                  </Row>
                  {discountAmount > 0 && (
                    <Row>
                      <Text textStyle="14px - Medium" style={{ flex: 1 }}>
                        Early Registration Discount
                      </Text>
                      <Text
                        textStyle="14px - Medium"
                        style={{ color: "#16A116" }}
                      >
                        {displayCurrency(discountAmount * -1)}
                      </Text>
                    </Row>
                  )}
                  <Row>
                    <Text textStyle="16px - Semibold" style={{ flex: 1 }}>
                      Order Total
                    </Text>
                    <Text textStyle="14px - Medium">
                      {displayCurrency(total)}
                    </Text>
                  </Row>
                </Column>
              </Column>
            )}
          </form.Subscribe>
          <ChargedTo />
          <Row gap={16}>
            <form.Subscribe
              selector={({ values, isSubmitting }) =>
                [values.numberOfStudents, isSubmitting] as const
              }
            >
              {([numberOfStudents, isSubmitting]) => (
                <Tooltip
                  content={
                    numberOfStudents
                      ? undefined
                      : "You must reserve a test for at least one student."
                  }
                >
                  <Button
                    disabled={!numberOfStudents || isSubmitting}
                    text={`Pay ${displayCurrency(total)} and Register`}
                    variant="primary"
                    type="submit"
                  />
                </Tooltip>
              )}
            </form.Subscribe>
            <Button
              text="Cancel"
              variant="subdued"
              onPress={() => {
                navigate({
                  search: {
                    modal: undefined,
                  },
                });
              }}
            />
          </Row>
          {registerMutation.isError && (
            <Text
              textStyle="14px - Bold"
              style={{ color: designTokens.colors.text.brand }}
            >
              {isApiError(registerMutation.error)
                ? registerMutation.error.response?.data.errors[0].message
                : "Something went wrong registering."}
            </Text>
          )}
        </form>
      </Modal>
    </Column>
  );
};
